import { useState, useRef } from "react";
import { Form } from "react-bootstrap";
import { gql, useQuery } from "@apollo/client";
import axios from "axios";

import SongsForm from "./SongsForm";

const query = gql`
  query {
    allShows {
      id
      showName
    }
  }
`;

function PlaylistForm({ playlist, state, setState, songs, setSongs }) {
  const { data } = useQuery(query);
  const playlistLinkRef = useRef();
  const [playlistLinkErrors, setPlaylistLinkErrors] = useState([]);
  const [playlistImportLoading, setPlaylistImportLoading] = useState(false);

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    setState((oldState) => {
      return {
        ...oldState,
        [name]: value,
      };
    });
  };

  const handlePlaylistLink = async (event) => {
    event.preventDefault();
    const playlistLink = playlistLinkRef.current.value;
    setPlaylistImportLoading(true);

    await axios({
      url: "/app/api/playlists/import",
      params: { url: playlistLink },
    })
      .then((res) => res.data)
      .then((data) => {
        setSongs((songs) => {
          // Base case if we don't have songs to import
          if (!data.length) return songs;
          let result = [];

          // Handle getting rid of empty songs
          for (let i = 0; i < songs.length; i++) {
            const song = songs[i];
            if (song.title || song.artist || song.album) result.push(song);
          }

          // Try to get our next unique id
          let nextId =
            Math.max(...songs.map((song) => parseInt(song._id) || 0)) + 1;

          // Handle importing the playlist without creating duplicates
          for (let i = 0; i < data.length; i++) {
            const importedSong = data[i];
            let addedBefore = false; // Keeps track if the song we're importing has been added before
            // Iterate through our old state to make sure we don't have duplicates
            for (let j = 0; j < songs.length; j++) {
              const song = songs[j];
              if (
                song.title === importedSong.title &&
                song.artist === importedSong.artist
              ) {
                // We found where the song has been added before
                addedBefore = true;
              }
            }

            // If it hasn't been added before, create a new song object
            if (!addedBefore) {
              result.push({ ...importedSong, _id: nextId });
              nextId += 1;
            }
          }
          return result;
        });
      })
      .catch((error) => {
        console.log("FULL ERROR:", error);

        if (error.response && error.response.data) {
          setPlaylistLinkErrors([error.response.data]);
        } else {
          setPlaylistLinkErrors([{ message: error.message || "Unknown error" }]);
        }
      });
    setPlaylistImportLoading(false);

    playlistLinkRef.current.value = "";
  };

  return (
    <>
      <Form className="mb-3" onSubmit={handlePlaylistLink}>
        <Form.Group control="playlistLink">
          <Form.Label>Playlist Link</Form.Label>
          {playlistLinkErrors.length ? (
            <p className="text-warning">
              {playlistLinkErrors.map(({ message }) => {
                return message + "\n";
              })}
            </p>
          ) : null}
          <div className="input-group">
            <Form.Control
              type="text"
              name="playlistLink"
              value={state.playlist_link}
              ref={playlistLinkRef}
              disabled={playlistImportLoading}
            />
            <button
              className="input-group-text"
              type="submit"
              disabled={playlistImportLoading}
            >
              {playlistImportLoading ? "Importing..." : "Import"}
            </button>
          </div>
          <Form.Text className="text-muted">
            Put your Spotify/YouTube/Soundcloud playlist URL here to import
            playlist.
          </Form.Text>
        </Form.Group>
      </Form>
      <Form>
        <Form.Group control="show">
          <Form.Label>Show</Form.Label>
          <Form.Control
            as="select"
            name="show"
            value={state.show}
            onChange={handleInputChange}
          >
            <option value={0} disabled>
              ---------
            </option>
            {playlist ? (
              <option value={playlist.show.id}>{playlist.show.showName}</option>
            ) : null}
            {data &&
              data.allShows.map((show) =>
                playlist && playlist.show.id === show.id ? null : (
                  <option key={show.id} value={show.id}>
                    {show.showName}
                  </option>
                )
              )}
          </Form.Control>
        </Form.Group>
        <Form.Group control="date">
          <Form.Label>Playlist Date</Form.Label>
          <Form.Control
            type="date"
            name="date"
            value={state.date}
            onChange={handleInputChange}
          />
        </Form.Group>
        <Form.Group control="title">
          <Form.Label>Playlist Title</Form.Label>
          <Form.Control
            type="text"
            name="title"
            value={state.title}
            onChange={handleInputChange}
          />
        </Form.Group>
        <Form.Group control="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            name="description"
            rows={6}
            style={{ minHeight: "160px" }}
            value={state.description}
            onChange={handleInputChange}
          />
        </Form.Group>
        <h2>Songs</h2>
        <Form.Group>
          <SongsForm state={songs} setState={setSongs} />
        </Form.Group>
      </Form>
    </>
  );
}

export default PlaylistForm;
